/**
 * 调参路径的解析与分类。纯函数、不依赖浏览器与 Vite，因此浏览器侧的 TuningController 与
 * dev server 的磁盘写回中间件共用同一份——两边各写一套「路径 → JSON 位置」迟早漂移，
 * 而写回写错字段是调参台最不能出的错。
 *
 * 路径形如 `<域>/<内容ID>/<字段…>`。数组成员按稳定名字寻址而非下标：下标随内容作者增删
 * 条目而漂移，overlay 会静默指到另一个对象上。名字的取法见 `memberKey`。
 */
export const DOMAINS = ["worlds", "slimes", "drops", "levels"] as const;
export type Domain = (typeof DOMAINS)[number];
export type RawDomains = Record<Domain, readonly { id: string }[]>;

/**
 * 数组成员的稳定名字。规则用 `instanceId`，波段用 `id`，混投表条目用 `definition`——
 * 一波里同一种史莱姆不会写两次，所以引用字段可以兼当身份。路径定位、面板控件生成与
 * 热改拷回三处必须用同一个取法，否则面板能显示的字段写回时定位不到，或拷回配错对象。
 * 没有名字的成员返回 undefined，调用方一律跳过，不得退回下标。
 */
export function memberKey(item: unknown): string | undefined {
  if (item === null || typeof item !== "object") return undefined;
  const { instanceId, id, definition } = item as {
    instanceId?: unknown;
    id?: unknown;
    definition?: unknown;
  };
  const key = instanceId ?? id ?? definition;
  return typeof key === "string" ? key : undefined;
}

/**
 * C 类（决策 0017）：启动期已被别人派生出去的值，热改只会让运行时与「存盘再加载」分道扬镳。
 * 它们进 overlay 并持久化，但不热拷回，须整页重载、在编译之前叠加后生效。
 * - `size/*`：网格维度、画布尺寸与输入夹取边界都在加载期一次性算定。
 * - `order`：参与 compileCatalog 末尾的关卡排序，改了会让就地拷回的下标对齐错位。
 * - `entities/*`：关卡布局在 spawnLevel 时展开成实体，改坐标只对下次开局有意义。
 */
const RELOAD_ONLY = [
  /^worlds\/[^/]+\/size\//,
  /^levels\/[^/]+\/order$/,
  /^levels\/[^/]+\/entities\//,
];
/** 不参与运行时的字段，改了只会让 overlay 说谎，一律拒绝。 */
const NOT_TUNABLE = [/\/schemaVersion$/, /^levels\/[^/]+\/editor\//];

export type TuningClass = "hot" | "reload";

/** 未知或不可调的路径返回 undefined；调用方据此显式失败，而不是静默忽略。 */
export function classify(path: string): TuningClass | undefined {
  if (NOT_TUNABLE.some((pattern) => pattern.test(path))) return undefined;
  return RELOAD_ONLY.some((pattern) => pattern.test(path)) ? "reload" : "hot";
}

export interface Located {
  host: Record<string, unknown>;
  key: string;
}

/** 只接受落在数值标量上的路径——非数值即视为未知路径。 */
export function locate(raw: RawDomains, path: string): Located | undefined {
  const [domain, contentId, ...rest] = path.split("/");
  if (
    !DOMAINS.includes(domain as Domain) ||
    contentId === undefined ||
    rest.length === 0
  )
    return undefined;
  const entry = raw[domain as Domain].find((item) => item.id === contentId);
  if (entry === undefined) return undefined;
  let node = entry as unknown as Record<string, unknown>;
  const segments = rest.slice(0, -1);
  for (let index = 0; index < segments.length; index += 1) {
    const next = node[segments[index]];
    if (Array.isArray(next)) {
      const key = segments[index + 1];
      if (key === undefined) return undefined;
      const item = next.find((candidate) => memberKey(candidate) === key);
      if (item === undefined) return undefined;
      node = item as Record<string, unknown>;
      index += 1;
      continue;
    }
    if (next === null || typeof next !== "object") return undefined;
    node = next as Record<string, unknown>;
  }
  const key = rest[rest.length - 1];
  return typeof node[key] === "number" ? { host: node, key } : undefined;
}

/** 就地叠加；找不到的路径返回，调用方决定是报错还是忽略。 */
export function applyOverlay(
  raw: RawDomains,
  overlay: Iterable<[string, number]>,
): string[] {
  const missing: string[] = [];
  for (const [path, value] of overlay) {
    const found = locate(raw, path);
    if (found === undefined) missing.push(path);
    else found.host[found.key] = value;
  }
  return missing;
}
